/**
 * Project sound at app boot (sound-and-music contract §5, §6).
 *
 * Two things have to happen before any frame exists:
 * - the host's AudioContext is made and armed on the page, so the first tap on our own
 *   chrome (Make this, Start, Play, the gated open) unlocks it, and the first tap inside
 *   the project then plays. Every flow that frames a project begins with one of those taps;
 * - the eval flags in the URL are read and kept, because the app rewrites its own URL
 *   (`/?view=<id>` becomes `/`) before the host first loads its index.
 *
 * The host also learns where the platform adapter is (iPad shell train 13): it asks the
 * adapter for sound playback on the first sound that actually plays, never here, and
 * releases it when the project's sound stops for good. Only the SPA boots this way; the
 * prod share island (web/) hands nothing in, and the host falls back to the browser's own
 * session (`platform/soundSession.ts` `webSoundPlayback`).
 *
 * Never throws: sound is never the reason a page fails to boot.
 */
import { createPlatformAdapter, isNativePlatform } from "../platform";
import { getSoundHost } from "./SoundHost";
import { projectSoundEnabled, soundAssetBaseDefaulted, soundIndexFixtureEnabled } from "./soundIndex";

export function bootProjectSound(): void {
  try {
    soundIndexFixtureEnabled();
    if (!projectSoundEnabled()) return;
    if (soundAssetBaseDefaulted()) {
      console.info("[sound] VITE_SOUND_ASSET_BASE is unset: the dev server plays the fixture index from /sound-fixture");
    }
    const host = getSoundHost();
    host.setPlaybackSession(() => createPlatformAdapter());
    // The shell lifts the gesture requirement, so the host does too (contract §5).
    host.setGestureFree(isNativePlatform());
    host.prime();
  } catch {
    /* sound is never the reason a page fails to boot */
  }
}
